<template>
  <v-card class="card-form">
    <v-card-title class="text-h5">
      <img
        class="card-form__logo"
        :src="require('@/assets/svg/logo.svg')"
        alt=""
      >
    </v-card-title>
    <hr class="card-form__hr-solid" />
    <v-card-text style="width: 25rem;">
      <div class="card-form__name">
        Register
      </div>

      <v-alert
        :model-value="!!errorText"
        closable
        type="error"
        @update:model-value="errorText = ''"
      >
        {{ errorText }}
      </v-alert>

      <div class="card-form__main">
        <v-form
          ref="form"
          @submit.prevent
        >
          <v-text-field
            v-model="formData.first_name"
            label="Your first name"
            color="primary"
            placeholder="Enter your first name"
            :rules="[rules.required]"
            variant="outlined"
            style="margin-bottom: 10px;"
          />

          <v-text-field
            v-model="formData.last_name"
            label="Your last name"
            color="primary"
            placeholder="Enter your last name"
            :rules="[rules.required]"
            variant="outlined"
            style="margin-bottom: 10px;"
          />

          <v-row>
            <v-col>
              <v-text-field
                v-model="formData.email"
                label="Your email"
                color="primary"
                placeholder="Enter your email"
                :rules="[rules.required, rules.isEmail]"
                variant="outlined"
              />
            </v-col>
          </v-row>
          <v-row style="margin-top: 0px;">
            <v-col>
              <phone-input
                ref="phoneInput"
                :showIcon="false"
                :rules="[rules.required]"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col
              cols="6"
              class="pb-0 pt-0"
            >
              <v-text-field
                v-model="formData.password"
                label="Password"
                color="primary"
                placeholder="Enter your password"
                :rules="[rules.required, rules.minLength, rules.bothRegisters]"
                type="password"
                variant="outlined"
                style="margin-bottom: 10px;"
              />
            </v-col>
            <v-col
              cols="6"
              class="pb-0 pt-0"
            >
              <v-text-field
                v-model="confirmPassword"
                label="Re-enter Password"
                color="primary"
                placeholder="Re-enter your password"
                :rules="[rules.required, rules.checkPassword]"
                type="password"
                variant="outlined"
              />
            </v-col>
          </v-row>
          <v-radio-group
            v-model="formData.gender"
            inline
            :rules="[rules.required]"
            class="mt-0 mb-2"
          >
            <v-radio
              label="Male"
              value="M"
            />
            <v-radio
              label="Female"
              value="F"
            />
          </v-radio-group>
          <v-checkbox
            color="primary"
            v-model="formData.informational_opt_in"
            label="I agree to receive essential SMS messages related to my lease, billing and housing updates."
          />
          <v-checkbox
            color="primary"
            v-model="formData.marketing_opt_in"
            label="I'd like to receive promotional, future leasing offers and marketing messages."
          />
          <p class="card-form__tnc-text">
            By continuing, I agree to the
            <a href="https://app.termly.io/policy-viewer/policy.html?policyUUID=874f765d-45a2-464f-9bd0-73f440476bcb"
              >Terms and Conditions</a
            >
            and
            <a href="https://app.termly.io/policy-viewer/policy.html?policyUUID=0bfddd13-3802-4d71-980c-6e7aff261e4a"
              >Privacy Policy</a
            >. I also agree to receive transactional and promotional emails and text messages. I may reply STOP or
            update my profile settings to cancel. Standard messaging and data rates apply.
          </p>
          <v-btn class="card-form__send-button" color="primary" size="large" @click.native="sendForm"> Sign Up </v-btn>
        </v-form>
      </div>
      <div class="card-form__footer">
        Already have an account?
        <a
          href="/"
          @click.prevent="$emit('change', 'login')"
        >
          Sign in here
        </a>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { Vue, Component, Prop, Emit } from 'vue-facing-decorator'
import validationRules from '../../validation/validationRules'
import PhoneInput from './../common/PhoneInput.vue'
import { authService } from '../../services/authServiceInstance'

@Component({
  components: { PhoneInput },
  emits: ['change']
})
export default class RegisterForm extends Vue {
  get authModal () {
    return this.$store.getters['ui/authModal']
  }

  rules = {
    required: v => validationRules.checkRequired(v),
    isEmail: v => validationRules.checkEmail(v),
    checkPassword: v => validationRules.compareStrings(v, this.formData.password),
    isPhone: v => validationRules.checkPhone(v),
    minLength: v => validationRules.minLength(v, 8),
    bothRegisters: v => validationRules.bothRegisters(v),
  }

  confirmPassword = ''
  errorText = ''
  formData = {
    email: '',
    first_name: '',
    last_name: '',
    cell_phone: '',
    password: '',
    gender: '',
    is_active: true,
    assigned_property: '',
    agreement_period: '',
    start_date: '',
    end_date: '',
    informational_opt_in: false,
    marketing_opt_in: false,
  }

  async isValid (): Promise<boolean> {
    return (this.$refs.form as typeof Vue & { validate: () => boolean }).validate()
  }

  async sendForm () {
    const isValid: any = await this.isValid()
    if (!isValid.valid) return false

    // @ts-ignore
    const cell_phone = this.$refs.phoneInput ? this.$refs.phoneInput?.getFullValue() : this.formData.cell_phone
    // TODO Use proper type for register payload.
    // TODO Not super sure why would we need data from 3 sources. Likely hack.
    const fullData = Object.assign({}, this.formData, this.authModal.data, { cell_phone })

    // HACK Backend requires filled property.
    if (!fullData.assigned_property) {
      if (this.$route.name === 'Property') {
        fullData.assigned_property = Array.isArray(this.$route.params.id) ? this.$route.params.id[0] : this.$route.params.id
      } else {
        const organizations = await this.$api.organization.getAll()
        fullData.assigned_property = organizations[0].properties[0].id
      }
    }

    try {
      await this.$api.auth.register(fullData)
      await authService.login({ username: fullData.email, password: fullData.password })
      authService.redirectAfterLogin()
    } catch (data: any) {
      this.errorText = data.detail
    }
  }
}
</script>

<style scoped>
.v-card__text {
  max-width: 28rem;
}
</style>
